import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Paperclip, X } from 'lucide-react';

import { apiPostForm, responseErrorMessage } from '../../lib/api_v2';
import { ERROR_REPORT_MODAL_EVENT, collectClientDiagnostics } from '../../lib/errorReportModal';
import { logError } from '../../lib/logger';

import { KEYCHAIN } from '../../contexts/KeyMaster';
import { useOptionalRouteWorkspace } from '../../contexts/RouteWorkspaceContext';
import { SETTINGS_MODAL_CLOSE_BUTTON_CLASS, SETTINGS_MODAL_CLOSE_ICON, SETTINGS_MODAL_TITLE_CLASS } from '../shared/settingsModalChrome';
import { Modal } from './Modal';
import { ModalBody } from './ModalBody';
import { ModalContent } from './ModalContent';
import { ModalPortal } from './ModalPortal';
import { ModalTopBar } from './ModalTopBar';

// Mirrors ErrorReport::MAX_ATTACHMENTS / MAX_ATTACHMENT_SIZE / MAX_TOTAL_ATTACHMENT_SIZE /
// MAX_BODY_LENGTH. The server enforces all of them; these only keep the form from pushing a
// body through the dyno that is certain to be rejected.
const MAX_FILES = 10;
const MAX_FILE_BYTES = 25 * 1024 * 1024;
const MAX_TOTAL_BYTES = 50 * 1024 * 1024;
const MAX_BODY_LENGTH = 10000;

// How long the thank-you sits before the modal dismisses itself. The portal's close
// transition adds a 200ms fade on top, so the whole thing is gone at ~2s.
const SUCCESS_AUTO_CLOSE_MS = 1800;

const INPUT_CLASS =
  'w-full rounded-lg border border-white bg-gray-50 px-3 py-2 text-[13px] leading-5 text-black placeholder:text-gray-400 hover:bg-white focus:outline-none sm:border-[0.5px] dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100 dark:placeholder:text-neutral-500';

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Every attachment limit the server enforces, checked in one place against the whole
// prospective selection so the three can't drift apart or be applied inconsistently.
const attachmentProblem = (nextFiles) => {
  if (nextFiles.length > MAX_FILES) return `Up to ${MAX_FILES} files per report.`;

  const oversized = nextFiles.find((file) => file.size > MAX_FILE_BYTES);
  if (oversized) return `"${oversized.name}" is ${formatBytes(oversized.size)} — the per-file limit is ${formatBytes(MAX_FILE_BYTES)}.`;

  const total = nextFiles.reduce((sum, file) => sum + file.size, 0);
  if (total > MAX_TOTAL_BYTES) return `Those files total ${formatBytes(total)} — the combined limit is ${formatBytes(MAX_TOTAL_BYTES)}.`;

  return null;
};

const ErrorReportForm = ({ onClose }) => {
  // Optional on purpose: reports can be submitted outside a workspace context.
  const routeWorkspace = useOptionalRouteWorkspace();
  const { workspaceId } = routeWorkspace || {};

  const [body, setBody] = useState('');
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [failedAttachments, setFailedAttachments] = useState([]);

  const fileInputRef = useRef(null);

  // The report is already persisted by the time this runs, so dismissing costs nothing.
  // Files that never reached storage are the exception: that notice has to stay up long
  // enough to read, so it waits for a deliberate close.
  useEffect(() => {
    if (!submitted || failedAttachments.length) return undefined;

    const timer = setTimeout(onClose, SUCCESS_AUTO_CLOSE_MS);
    return () => clearTimeout(timer);
  }, [submitted, failedAttachments, onClose]);

  const handleAddFiles = useCallback(
    (event) => {
      const picked = Array.from(event.target.files || []);
      // Let the same file be picked again after a remove.
      event.target.value = '';
      if (!picked.length) return;

      const next = [...files, ...picked];
      const problem = attachmentProblem(next);
      setError(problem);
      if (!problem) setFiles(next);
    },
    [files],
  );

  const handleRemoveFile = useCallback((index) => {
    setError(null);
    setFiles((current) => current.filter((_, position) => position !== index));
  }, []);

  const handleSubmit = useCallback(
    async function handleSubmitCallback(event) {
      event.preventDefault();
      if (isSubmitting) return;
      if (!body.trim() && !files.length) {
        setError('Add a note or attach a file.');
        return;
      }

      setIsSubmitting(true);
      setError(null);

      const formData = new FormData();
      if (workspaceId) formData.append('workspace_id', workspaceId);
      formData.append('error_report[body]', body);
      formData.append('error_report[occurred_at]', new Date().toISOString());
      formData.append('error_report[diagnostics]', JSON.stringify(collectClientDiagnostics()));
      files.forEach((file) => formData.append('files[]', file, file.name));

      try {
        const response = await apiPostForm('/error_reports', formData);
        const data = await response.json();

        if (data.ok) {
          // A 200 means something was saved; the server rolls back and errors when a
          // submission ends up with neither a note nor a stored file, so there is no
          // empty-report case to second-guess here — just name whatever failed on the way.
          setBody('');
          setFiles([]);
          setFailedAttachments(data.data?.failed_attachments || []);
          setSubmitted(true);
        } else {
          setError(responseErrorMessage(data, 'Submission failed.'));
        }
      } catch (requestError) {
        logError('ErrorReportModal', 'Could not submit error report', { error: requestError });
        setError('Request failed. Check the console.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [body, files, isSubmitting, workspaceId],
  );

  const canSubmit = (body.trim().length > 0 || files.length > 0) && !isSubmitting;

  return (
    <Modal
      onClose={onClose}
      className="rounded-2xl"
      closePlacement="top-left"
      closeButtonClassName={SETTINGS_MODAL_CLOSE_BUTTON_CLASS}
      closeIcon={SETTINGS_MODAL_CLOSE_ICON}
    >
      <ModalBody className="min-h-0">
        <ModalTopBar className="!pb-0">
          <span className={SETTINGS_MODAL_TITLE_CLASS}>Report a problem</span>
        </ModalTopBar>
        <ModalContent className="min-h-0 md:!px-5">
          {submitted ? (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-4 text-center">
              <span className="text-[15px] leading-5 font-medium text-black dark:text-neutral-200">Message received!</span>
              {failedAttachments.length > 0 && (
                <span className="text-xs text-red-400">
                  {failedAttachments.length === 1 ? '1 file' : `${failedAttachments.length} files`} could not be attached:{' '}
                  {failedAttachments.join(', ')}
                </span>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex w-full flex-col gap-4 pt-5">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="error_report_body" className="text-[11px] leading-4 font-medium text-gray-500 dark:text-neutral-400">
                  What happened?
                </label>
                <textarea
                  id="error_report_body"
                  value={body}
                  onChange={(event) => {
                    setBody(event.target.value);
                    setError(null);
                  }}
                  placeholder="What were you doing when it broke?"
                  rows={4}
                  autoFocus
                  maxLength={MAX_BODY_LENGTH}
                  className={`${INPUT_CLASS} resize-none`}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <span className="text-[11px] leading-4 font-medium text-gray-500 dark:text-neutral-400">
                  Files ({files.length}/{MAX_FILES})
                </span>
                <input ref={fileInputRef} type="file" multiple onChange={handleAddFiles} className="hidden" />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={files.length >= MAX_FILES}
                  className="flex h-10 w-full cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 text-[13px] leading-5 text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-900"
                >
                  <Paperclip className="size-4" />
                  Attach files
                </button>
                {files.length > 0 && (
                  <ul className="flex flex-col gap-1 pt-1">
                    {files.map((file, index) => (
                      <li
                        key={`${file.name}-${index}`}
                        className="flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-1.5 dark:bg-neutral-900"
                      >
                        <span
                          className="min-w-0 flex-1 truncate text-[12px] leading-5 text-gray-700 dark:text-neutral-200"
                          title={file.name}
                        >
                          {file.name}
                        </span>
                        <span className="shrink-0 font-mono text-[11px] leading-5 text-gray-400 dark:text-neutral-500">
                          {formatBytes(file.size)}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(index)}
                          aria-label={`Remove ${file.name}`}
                          className="shrink-0 cursor-pointer text-gray-400 hover:text-black dark:hover:text-neutral-100"
                        >
                          <X className="size-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {error && <span className="block text-left text-xs text-red-400">{error}</span>}

              <button
                type="submit"
                data-app-region="none"
                disabled={!canSubmit}
                className="flex h-12 w-full cursor-pointer items-center justify-center rounded-full bg-idle-blue text-[15px] leading-5 font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 dark:disabled:bg-neutral-800 dark:disabled:text-neutral-500"
              >
                {isSubmitting ? 'Submitting…' : 'Submit report'}
              </button>
            </form>
          )}
        </ModalContent>
      </ModalBody>
    </Modal>
  );
};

/**
 * Self-mounting harness for POST /v2/error_reports. Renders nothing until something
 * dispatches `output:internal:error-report-modal` on the window:
 *
 *   window.dispatchEvent(new CustomEvent('output:internal:error-report-modal'))
 */
const ErrorReportModal = () => {
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  useEffect(() => {
    window.addEventListener(ERROR_REPORT_MODAL_EVENT, handleOpen);
    return () => {
      window.removeEventListener(ERROR_REPORT_MODAL_EVENT, handleOpen);
    };
  }, [handleOpen]);

  return (
    <ModalPortal
      open={open}
      onClose={handleClose}
      keychainId={KEYCHAIN.MODAL__ERROR_REPORT}
      className="h-[380px] max-h-[calc(100dvh-32px)] max-w-[440px]"
    >
      <ErrorReportForm onClose={handleClose} />
    </ModalPortal>
  );
};

export default ErrorReportModal;
